import type { CookieConsentConfig } from "vanilla-cookieconsent";

/**
 * Configurazione vanilla-cookieconsent v3
 * Conforme alle linee guida del Garante Privacy italiano (R1-R6)
 */

// Revision number: incrementare ad ogni modifica sostanziale della cookie/privacy policy
const CONSENT_REVISION = 1;

// Cookie expiry in giorni (R6: 180 giorni = 6 mesi)
const CONSENT_EXPIRY_DAYS = 180;

export type ConsentCategory = "necessary" | "analytics" | "marketing";

export interface ConsentCallbacks {
  onConsentGiven: (acceptedCategories: ConsentCategory[]) => void;
  onConsentChanged: (acceptedCategories: ConsentCategory[]) => void;
}

export function createCookieConsentConfig(
  callbacks: ConsentCallbacks
): CookieConsentConfig {
  return {
    // R6: cookie consent valido 180 giorni (sia accettazione che rifiuto)
    cookie: {
      name: "cc_cookie",
      expiresAfterDays: CONSENT_EXPIRY_DAYS,
    },

    // R1: opt-in obbligatorio — nessun cookie non tecnico attivato di default
    // R5: revision per versioning della policy
    revision: CONSENT_REVISION,

    guiOptions: {
      consentModal: {
        // Barra a tutta larghezza incollata al fondo: su mobile non copre la
        // hero (a differenza di "box wide" centrato). Layout nativo.
        layout: "bar",
        position: "bottom",
        equalWeightButtons: true, // R2: parita visiva tra bottoni
      },
      preferencesModal: {
        layout: "box",
        position: "right",
        equalWeightButtons: true,
      },
    },

    // R2: X chiude il banner = rifiuto totale
    // In v3, quando l'utente chiude il modal senza accettare, equivale a rifiuto
    onFirstConsent: ({ cookie }) => {
      const accepted = cookie.categories as ConsentCategory[];
      callbacks.onConsentGiven(accepted);
    },

    onChange: ({ cookie }) => {
      const accepted = cookie.categories as ConsentCategory[];
      callbacks.onConsentChanged(accepted);
    },

    categories: {
      // Cookie tecnici: sempre attivi, non disabilitabili (R4)
      necessary: {
        enabled: true,
        readOnly: true, // R4: toggle bloccato per cookie necessari
      },
      // Cookie analitici: OFF di default (R1)
      analytics: {
        enabled: false,
        readOnly: false,
      },
      // Cookie marketing: OFF di default (R1)
      marketing: {
        enabled: false,
        readOnly: false,
      },
    },

    language: {
      default: "it",
      translations: {
        it: {
          consentModal: {
            // R2: banner con "Accetta tutti", "Personalizza", X
            // R3: link alla Privacy Policy nel banner
            title: "Utilizziamo i cookie",
            description:
              'Questo sito utilizza cookie tecnici necessari e, previo tuo consenso, cookie analitici e di marketing per migliorare la tua esperienza. Puoi scegliere quali categorie attivare. Per maggiori informazioni consulta la nostra <a href="/privacy" class="cc-link">Privacy Policy</a> e la <a href="/cookie-policy" class="cc-link">Cookie Policy</a>.',
            acceptAllBtn: "Accetta tutti",
            acceptNecessaryBtn: "Solo necessari",
            showPreferencesBtn: "Personalizza",
            closeIconLabel: "Rifiuta e chiudi", // R2: X = rifiuto
          },
          preferencesModal: {
            // R4: pannello preferenze granulari
            title: "Preferenze cookie",
            acceptAllBtn: "Accetta tutti",
            acceptNecessaryBtn: "Solo necessari",
            savePreferencesBtn: "Salva preferenze",
            closeIconLabel: "Chiudi",
            sections: [
              {
                title: "Gestisci le tue preferenze",
                description:
                  "Puoi attivare o disattivare le diverse categorie di cookie. I cookie necessari non possono essere disattivati perche indispensabili per il funzionamento del sito.",
              },
              {
                title: "Cookie necessari",
                description:
                  "Questi cookie sono essenziali per il corretto funzionamento del sito. Includono cookie di sessione, autenticazione e sicurezza. Non possono essere disattivati.",
                linkedCategory: "necessary",
              },
              {
                title: "Cookie analitici",
                description:
                  "Questi cookie ci permettono di raccogliere dati anonimi sull'utilizzo del sito (es. Google Analytics) per migliorare le prestazioni e i contenuti.",
                linkedCategory: "analytics",
              },
              {
                title: "Cookie di marketing",
                description:
                  "Questi cookie vengono utilizzati per mostrare annunci pertinenti e misurare l'efficacia delle campagne pubblicitarie (es. Facebook Pixel, Hotjar).",
                linkedCategory: "marketing",
              },
              {
                title: "Maggiori informazioni",
                description:
                  'Per qualsiasi domanda relativa alla nostra politica sui cookie, puoi consultare la <a href="/cookie-policy" class="cc-link">Cookie Policy</a> completa o contattarci.',
              },
            ],
          },
        },
      },
    },
  };
}
